<script setup>
import { useI18n } from 'vue-i18n'
import { createAccount, createGoogleAccount } from '@/services/api/auth.js'

const emit = defineEmits(['back'])
const { t } = useI18n()

const handleSubmit = async () => {
    await createAccount()
    message.info(t('components.authentication.signIn.featureInDevelopment'))
}

const handleGoogleSignup = async () => {
    await createGoogleAccount()
    message.info(t('components.authentication.signIn.googleIntegrationSoon'))
}
</script>

<template>
    <form class="create-account-form" @submit.prevent="handleSubmit">
        <div class="create-account-form__group">
            <label for="create-full-name">{{ $t('components.authentication.signIn.fullName') }}</label>
            <div class="create-account-form__field">
                <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path d="M20 21a8 8 0 0 0-16 0" />
                    <circle cx="12" cy="7" r="4" />
                </svg>
                <input
                    id="create-full-name"
                    type="text"
                    :placeholder="$t('components.authentication.signIn.fullNamePlaceholder')"
                    autocomplete="name"
                    required
                >
            </div>
        </div>

        <div class="create-account-form__group">
            <label for="create-username">{{ $t('components.authentication.signIn.username') }}</label>
            <div class="create-account-form__field">
                <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path d="M16 21v-2a4 4 0 0 0-8 0v2" />
                    <circle cx="12" cy="7" r="4" />
                    <path d="M6 21h12" />
                </svg>
                <input
                    id="create-username"
                    type="text"
                    :placeholder="$t('components.authentication.signIn.usernamePlaceholder')"
                    autocomplete="username"
                    required
                >
            </div>
        </div>

        <div class="create-account-form__group">
            <label for="create-email">{{ $t('components.authentication.signIn.emailAddress') }}</label>
            <div class="create-account-form__field">
                <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                </svg>
                <input
                    id="create-email"
                    type="email"
                    :placeholder="$t('components.authentication.signIn.emailAddressPlaceholder')"
                    autocomplete="email"
                    required
                >
            </div>
        </div>

        <button class="create-account-form__submit" type="submit">
            <span>{{ $t('components.authentication.signIn.createAccountButton') }}</span>
            <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
        </button>

        <div class="create-account__divider">
            <span></span>
            <small>{{ $t('components.authentication.signIn.orCreateWith') }}</small>
            <span></span>
        </div>

        <button class="google-button" type="button" @click="handleGoogleSignup">
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            {{ loadingGoogle ? $t('components.authentication.signIn.googleLoginLoading') : $t('components.authentication.signIn.googleSignup') }}
        </button>

        <button class="create-account-form__back" type="button" @click="emit('back')">
            <span class="material-symbols-outlined">arrow_back</span>
            {{ $t('components.authentication.signIn.backToLogin') }}
        </button>
    </form>
</template>

<style lang="scss" scoped>
.create-account-form {
    display: flex;
    flex-direction: column;
    gap: 20px;

    &__group {
        label {
            display: block;
            margin-bottom: 8px;
            color: #5b5b98;
            font-size: 13px;
            font-weight: 600;
        }
    }

    &__field {
        position: relative;
        color: #a9a9c8;

        > svg {
            position: absolute;
            top: 50%;
            left: 13px;
            transform: translateY(-50%);
            transition: color 0.18s;
            pointer-events: none;
        }

        input {
            width: 100%;
            min-height: 46px;
            padding: 13px 16px 13px 40px;
            border: 1.5px solid #eceef2;
            border-radius: 10px;
            outline: none;
            background: #f8f9ff;
            color: #260944;
            font-family: Inter, sans-serif;
            font-size: 14px;
            transition: border-color 0.18s, background 0.18s, box-shadow 0.18s;

            &::placeholder {
                color: #a9a9c8;
            }

            &:focus {
                border-color: #605dff;
                background: #fff;
                box-shadow: 0 0 0 4px rgba(96, 93, 255, 0.08);
            }
        }

        &:focus-within {
            color: #605dff;
        }
    }

    &__submit,
    &__back {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        width: 100%;
        border-radius: 10px;
        font-family: Inter, sans-serif;
        font-size: 15px;
        font-weight: 700;
        cursor: pointer;

        &:disabled {
            cursor: not-allowed;
            opacity: 0.72;
        }
    }

    &__submit {
        margin-top: 4px;
        padding: 15px;
        border: 0;
        background: linear-gradient(135deg, #ffd056 0%, #ffe7389e 100%);
        box-shadow: 0 6px 24px rgba(96, 93, 255, 0.38);
        color: #2c2c2cd0;
    }

    &__back {
        padding: 12px;
        border: 1.5px solid #eceef2;
        background: #fff;
        color: #605dff;

        .material-symbols-outlined {
            font-size: 18px;
        }
    }

    .create-account__divider {
        display: flex;
        align-items: center;
        gap: 14px;
        span {
            flex: 1;
            height: 1px;
            background: #eceef2;
        }

        small {
            color: #a9a9c8;
            font-size: 12px;
            font-weight: 500;
            white-space: nowrap;
        }
    }

    .google-button {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        width: 100%;
        padding: 13px;
        border: 1.5px solid #eceef2;
        border-radius: 10px;
        background: #fff;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
        color: #260944;
        font-family: Inter, sans-serif;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;

        &:disabled {
            cursor: not-allowed;
            opacity: 0.72;
        }
    }

}
</style>
